import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { AlertCircle, Loader, type LucideIcon } from "lucide-react";
import type { FpToolCallMessagePart } from "../../../../../../schemas/codegen";
import { ToolError } from "./ToolError";

type ExpandableMessageCardProps = {
  // part: Extract<FpMessagePart, { type: "tool-call" }>;
  icon: LucideIcon;
  title: React.ReactNode;
  additionalContent?: React.ReactNode;
  className?: string;
  accordionValue?: string;
  children?: React.ReactNode;
  expanded?: boolean;
} & Pick<FpToolCallMessagePart, "status" | "result">;

export function ExpandableMessageCard({
  icon: Icon,
  title,
  additionalContent,
  className,
  accordionValue = "expandable-card",
  children,
  expanded = false,
  status,
  result,
}: ExpandableMessageCardProps) {
  const isError = status === "error";

  const statusIcon =
    status === "pending" ? (
      <Loader className="w-3 h-3 animate-spin" />
    ) : isError ? (
      <AlertCircle className="w-3 h-3 text-warning" />
    ) : null;

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={expanded ? accordionValue : undefined}
      className={cn("w-full", className)}
    >
      <AccordionItem value={accordionValue} className="rounded-md border-b-0">
        <AccordionTrigger
          className={cn(
            "flex items-center gap-2 px-3 py-2 transition-colors",
            "bg-input/80 rounded-md hover:bg-muted",
            "data-[state=open]:rounded-b-none",
            "data-[state=open]:border-b",
          )}
        >
          <div className="flex items-center gap-2 w-full">
            <Icon className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
            <div className="flex-1 text-left min-w-0 items-center flex gap-2">
              <span className="text-sm font-medium inline-flex items-center">
                {title}
              </span>
              {additionalContent}
            </div>
            <div className="flex-shrink-0 mr-1">{statusIcon}</div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="bg-input/40 rounded-b-md gap-4 px-3 pt-4">
          {children}
          {isError && result ? <ToolError result={result} /> : null}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
